import {AfterViewInit, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {PartitionSelectionService} from "../../../services/partition/partition-selection.service";
import {DiagnosticOpeService} from "../../../services/ope/diagnosticOpe.service";
import {DateIntervalDTO, GetDiagnosticEventsCB} from "../../../ope/diagnostic/GetDiagnosticEventsCB";
import {GetDiagnosticStatusCB} from "../../../ope/diagnostic/GetDiagnosticStatusCB";
import {DeviceAlarm, GatewayAlarm, PartitionAlarm} from "../../../models/diagnostic/PartitionAlarm";
import {AlarmOriginEntityEnum} from "../../../enums/diagnostic/AlarmOriginEntityEnum";
import {AlarmEvent} from "../../../models/diagnostic/AlarmEvent";
import {ChangePartitionEvent} from "../../../events/ChangePartitionEvent";
import {DeviceAlarmStatusEnum} from "../../../models/notification/DeviceAlarmStatus";
import {GatewayAlarmStatusEnum} from "../../../models/notification/GatewayAlarmStatus";
import {BaseAlarmComponent} from "../common/baseAlarm.component";
import {DeviceAlarmNotificationService} from "../../../services/notification/interfaces/DeviceAlarmNotificationService";
import {
  GatewayAlarmNotificationService
} from "../../../services/notification/interfaces/GatewayAlarmNotificationService";
import {NotificationService} from "../../../services/notification/notification.service";
import {TypeOfDateSettingsEnumAlarms} from "../../../enums/dashboard/TypeOfDateSettingsEnumAlarms";

@Component({
  selector: 'app-diagnostic',
  templateUrl: './diagnostic.component.html',
  styleUrls: ['./diagnostic.component.css', '../common/baseAlarm.component.css']
})
export class DiagnosticComponent extends BaseAlarmComponent implements OnInit, AfterViewInit, OnDestroy {

  protected loadingData: boolean = true;

  protected partitionAlarms: PartitionAlarm[];

  protected deviceFilters: number[];

  protected gatewayFilters: number[];

  protected partitionSelection: PartitionSelectionService = inject(PartitionSelectionService);

  protected diagnosticService: DiagnosticOpeService = inject(DiagnosticOpeService);

  protected selectedItem?: DeviceAlarm | GatewayAlarm;

  protected selectedItemPartitionId?: number;

  protected selectedOriginEntity?: AlarmOriginEntityEnum;

  protected selectedItemEvents: AlarmEvent[];

  protected readonly AlarmOriginEntityEnum = AlarmOriginEntityEnum;

  /**
   * Service used for push notification subscriptions and event handling of devices
   * @protected
   */
  protected deviceAlarmNotificationService: DeviceAlarmNotificationService = inject(NotificationService);

  /**
   * List of ids of subscribed devices to their alarm push notification
   * @protected
   */
  protected subscribedNotificationIdsDevices: number[] = [];

  /**
   * Service used for push notification subscriptions and event handling of gws
   * @protected
   */
  protected gatewayAlarmNotificationService: GatewayAlarmNotificationService = inject(NotificationService);

  /**
   * List of ids of subscribed gw to their alarm push notification
   * * @protected
   */
  protected subscribedNotificationIdsGateways: number[] = [];

  constructor() {
    super();
    this.selectedItemEvents = [];
    this.partitionAlarms = [];
    this.deviceFilters = [0, 1, 2];
    this.gatewayFilters = [0, 1];
  }

  ngOnInit() {

    super.ngOnInit();
    this.subscriptions = [
      this.eventBus.onEvent(ChangePartitionEvent.NAME, async (event: ChangePartitionEvent) => {
        this.loadDiagnosticStatus();
      }),

      this.deviceAlarmNotificationService.onDeviceStatusChanged(event => {
        this.partitionAlarms.forEach(partitionAlarm => {
          if (!!partitionAlarm.gateways) {
            partitionAlarm.gateways.forEach(gateway => {
              let deviceToChange = gateway.devices.find(device => device.id === event.value.deviceId);
              if (!!deviceToChange)
                deviceToChange.alarmStatus = event.value.status;
            })
          }
        });
        if (!!this.selectedItem && this.selectedOriginEntity === AlarmOriginEntityEnum.DEVICE && this.selectedItem.id === event.value.deviceId) {
          if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS || this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.REALTIME || (this.startDate >= new Date(event.value.ts) && this.endDate <= new Date(event.value.ts))) {
            let newEvent = new AlarmEvent();
            newEvent.ts = event.value.ts;
            if (event.value.message !== "null") {
              newEvent.message = event.value.message;
            }
            newEvent.alarmStatus = this.translate.instant("DIAGNOSTIC." + DeviceAlarmStatusEnum[event.value.status]);
            this.selectedItemEvents = [newEvent].concat(this.selectedItemEvents);
            if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS) {
              if (this.selectedItemEvents.length > this.lastEventsNumber) {
                this.selectedItemEvents.pop();
              }
            }
            this.sortEvents();
          }
        }
      }),

      this.gatewayAlarmNotificationService.onGatewayStatusChanged(event => {
        this.partitionAlarms.forEach(partitionAlarm => {
          let gatewayToChange = partitionAlarm.gateways?.find(gateway => gateway.id === event.value.gatewayId);
          if (!!gatewayToChange)
            gatewayToChange.alarmStatus = event.value.status;
        });
        if (!!this.selectedItem && this.selectedOriginEntity === AlarmOriginEntityEnum.GATEWAY && this.selectedItem.id === event.value.gatewayId) {
          if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS || this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.REALTIME || (this.startDate >= new Date(event.value.ts) && this.endDate <= new Date(event.value.ts))) {
            let newEvent = new AlarmEvent();
            newEvent.ts = event.value.ts;
            newEvent.alarmStatus = this.translate.instant("DIAGNOSTIC." + GatewayAlarmStatusEnum[event.value.status]);
            this.selectedItemEvents = [newEvent].concat(this.selectedItemEvents);
            if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS) {
              if (this.selectedItemEvents.length > this.lastEventsNumber) {
                this.selectedItemEvents.pop();
              }
            }
            this.sortEvents();
          }
        }
      })
    ]
  }

  ngAfterViewInit() {
    this.loadDiagnosticStatus();
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.gatewayAlarmNotificationService.removeGatewayIdsToAlarmNotifications(this.subscribedNotificationIdsGateways);
    this.deviceAlarmNotificationService.removeDeviceIdsToAlarmNotifications(this.subscribedNotificationIdsDevices)
  }

  loadDiagnosticStatus() {
    this.loadingData = true;
    if (!!this.partitionSelection.selectedPartitionId || this.partitionSelection.selectedPartitionId === 0) {
      let cb = new GetDiagnosticStatusCB();
      cb.i.partitionId = this.partitionSelection.selectedPartitionId;
      cb.i.gatewayAlarmStatusSet = this.gatewayFilters;
      cb.i.deviceAlarmStatusSet = this.deviceFilters;
      this.diagnosticService.getDiagnosticStatus(cb).then(
        () => {
          this.partitionAlarms = cb.o.partitions;

          this.gatewayAlarmNotificationService.removeGatewayIdsToAlarmNotifications(this.subscribedNotificationIdsGateways);
          this.deviceAlarmNotificationService.removeDeviceIdsToAlarmNotifications(this.subscribedNotificationIdsDevices);

          this.subscribedNotificationIdsGateways = [];
          this.subscribedNotificationIdsDevices = [];
          this.partitionAlarms.forEach(
            partition => {
              if (!!partition.gateways)
                partition.gateways.forEach(
                  gateway => {
                    this.subscribedNotificationIdsGateways.push(gateway.id);
                    gateway.devices.forEach(
                      device => {
                        this.subscribedNotificationIdsDevices.push(device.id);
                      }
                    )
                  }
                );
            }
          );
          this.gatewayAlarmNotificationService.addGatewayIdsToAlarmNotifications(this.subscribedNotificationIdsGateways);
          this.deviceAlarmNotificationService.addDeviceIdsToAlarmNotifications(this.subscribedNotificationIdsDevices);
          this.loadingData = false
        }).catch(
        () => {
          this.loadingData = false;
        });
    } else {
      this.loadingData = false;
    }
  }

  getDiagnosticEvents(cb: GetDiagnosticEventsCB) {
    this.loadingData = true;
    this.diagnosticService.getDiagnosticEvents(cb).then(() => {
      this.selectedItemEvents = cb.o.events;
      this.sortEvents();
      this.selectedItemEvents.forEach(x => {
        if (this.selectedOriginEntity === AlarmOriginEntityEnum.DEVICE)
          x.alarmStatus = this.translate.instant("DIAGNOSTIC." + DeviceAlarmStatusEnum[x.alarmStatusValue]);
        else if (this.selectedOriginEntity === AlarmOriginEntityEnum.GATEWAY)
          x.alarmStatus = this.translate.instant("DIAGNOSTIC." + GatewayAlarmStatusEnum[x.alarmStatusValue])
      });
      this.loadingData = false;
    }).catch(
      () => {
        this.isDrawerOpen = false;
        this.loadingData = false;
      });
  }

  sortEvents() {
    this.selectedItemEvents.sort((a, b) => {
      return new Date(b.ts).getTime() - new Date(a.ts).getTime()
    });
  }

  initializeClickedItemDiagnostics(partition: PartitionAlarm, item: GatewayAlarm | DeviceAlarm, originEntityName: AlarmOriginEntityEnum) {
    if (!!this.partitionSelection.selectedPartitionId) {
      this.isDrawerOpen = true;
      this.selectedItem = item;
      this.selectedItemPartitionId = partition.id;
      this.selectedOriginEntity = originEntityName;

      let cb = new GetDiagnosticEventsCB();

      if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS) {
        cb.i.lastEvents = true;
        cb.i.lastEventsNumber = this.lastEventsNumber;
      } else if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.FIXED_DATE) {
        cb.i.lastEvents = false;
        cb.i.dateInterval = new DateIntervalDTO();
        cb.i.dateInterval.toTs = new Date(this.endDate).getTime();
        cb.i.dateInterval.fromTs = new Date(this.startDate).getTime();
      } else {
        cb.i.lastEvents = false;
        cb.i.dateInterval = new DateIntervalDTO();
        cb.i.dateInterval.toTs = new Date().getTime();
        cb.i.dateInterval.fromTs = this.getTsFromRealTime().getTime()
      }
      cb.i.entityId = item.id;
      cb.i.partitionId = partition.id;
      cb.i.originEntityName = originEntityName;
      this.getDiagnosticEvents(cb);
    }
  }

  reloadDiagnosticEvents() {
    if (!!this.selectedItem && (!!this.selectedItemPartitionId || this.selectedItemPartitionId === 0) && !!this.selectedOriginEntity) {
      let cb = new GetDiagnosticEventsCB();
      if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS) {
        cb.i.lastEvents = true;
        cb.i.lastEventsNumber = this.lastEventsNumber;
      } else if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.FIXED_DATE) {
        cb.i.lastEvents = false;
        cb.i.dateInterval = new DateIntervalDTO();
        cb.i.dateInterval.toTs = new Date(this.endDate).getTime();
        cb.i.dateInterval.fromTs = new Date(this.startDate).getTime();
      } else {
        cb.i.lastEvents = false;
        cb.i.dateInterval = new DateIntervalDTO();
        cb.i.dateInterval.toTs = new Date().getTime();
        cb.i.dateInterval.fromTs = this.getTsFromRealTime().getTime()
      }
      cb.i.entityId = this.selectedItem.id;
      cb.i.partitionId = this.selectedItemPartitionId;
      cb.i.originEntityName = this.selectedOriginEntity;
      this.getDiagnosticEvents(cb);
    }
  }

  filterGateway(filter: number) {
    if (this.gatewayFilters.includes(filter)) {
      this.gatewayFilters.splice(this.gatewayFilters.indexOf(filter), 1);
    } else {
      this.gatewayFilters.push(filter)
    }
  }

  filterDevice(filter: number) {
    if (this.deviceFilters.includes(filter)) {
      this.deviceFilters.splice(this.deviceFilters.indexOf(filter), 1);
    } else {
      this.deviceFilters.push(filter)
    }
  }

  openGatewayDetail(partition: PartitionAlarm, gateway: GatewayAlarm) {
    if (!this.selectedOriginEntity && !this.selectedItem)
      this.initializeClickedItemDiagnostics(partition, gateway, AlarmOriginEntityEnum.GATEWAY);
    else if (this.selectedOriginEntity === AlarmOriginEntityEnum.GATEWAY && this.selectedItem!.id === gateway.id)
      this.isDrawerOpen = false;
    else
      this.initializeClickedItemDiagnostics(partition, gateway, AlarmOriginEntityEnum.GATEWAY)
  }

  openDeviceDetail(partition: PartitionAlarm, device: DeviceAlarm) {
    if (!this.selectedOriginEntity && !this.selectedItem)
      this.initializeClickedItemDiagnostics(partition, device, AlarmOriginEntityEnum.DEVICE);
    else if (this.selectedOriginEntity === AlarmOriginEntityEnum.DEVICE && this.selectedItem!.id === device.id)
      this.isDrawerOpen = false;
    else
      this.initializeClickedItemDiagnostics(partition, device, AlarmOriginEntityEnum.DEVICE)
  }

  resetDrawerVariables(drawerOpened: boolean) {
    if (!drawerOpened) {
      this.selectedOriginEntity = undefined;
      this.selectedItem = undefined;
      this.selectedItemPartitionId = undefined;
    }
  }

  getAlarmStatusCssClass(item: GatewayAlarm | DeviceAlarm) {
    switch (item.alarmStatus) {
      case DeviceAlarmStatusEnum.ONLINE || GatewayAlarmStatusEnum.ONLINE:
        return "ok";
      case DeviceAlarmStatusEnum.OFFLINE || GatewayAlarmStatusEnum.OFFLINE:
        return "offline";
      case DeviceAlarmStatusEnum.WARNING:
        return "warning";
      default:
        return "offline unknown"
    }
  }

  getSelectedGatewayStatus() {
    if (this.selectedOriginEntity === AlarmOriginEntityEnum.GATEWAY) {
      let status = `REGISTRY.GATEWAY.STATES.${(this.selectedItem as GatewayAlarm).status}`;
      return "<strong>" + this.translate.instant("DIAGNOSTIC.GATEWAY_STATUS") + ": " + "</strong>" + this.translate.instant(status);
    }
    return "";
  }

  getCurrentItemStatus() {
    let currentStatus: string = this.translate.instant("DIAGNOSTIC.UNKNOWN");
    if (!!this.selectedItem) {
      switch (this.selectedItem.alarmStatus) {
        case DeviceAlarmStatusEnum.ONLINE || GatewayAlarmStatusEnum.ONLINE:
          currentStatus = this.translate.instant("DIAGNOSTIC.ONLINE");
          break;
        case DeviceAlarmStatusEnum.OFFLINE || GatewayAlarmStatusEnum.OFFLINE:
          currentStatus = this.translate.instant("DIAGNOSTIC.OFFLINE");
          break;
        case DeviceAlarmStatusEnum.WARNING:
          currentStatus = this.translate.instant("DIAGNOSTIC.WARNING");
          break;
        default:
          currentStatus = this.translate.instant("DIAGNOSTIC.UNKNOWN");
          break;
      }
      let stylizedCurrentStatus = `<span class="${this.getAlarmStatusCssClass(this.selectedItem)}">${currentStatus}</span>`
      return "<strong>" + this.translate.instant("DIAGNOSTIC.CURRENT_STATUS") + ": " + "</strong>" + stylizedCurrentStatus;
    }
    return "<strong>" + this.translate.instant("DIAGNOSTIC.CURRENT_STATUS") + ": " + "</strong>" + currentStatus;
  }
}
